import { ref } from 'vue';
import { defineStore } from 'pinia';

export type SessionStatus = 'active' | 'suspect' | 'expired' | 'unreachable';

export type SessionSignal = '401' | '419' | 'iframe-login' | 'probe' | 'echo' | 'storage';

export type AuthProvider = 'apx' | 'f360';

type SuspectHandler = (signal: SessionSignal) => void;

const suspectHandlers = new Set<SuspectHandler>();

/**
 * Register a callback invoked whenever the session becomes suspect.
 *
 * Kept outside the store so the store itself has no knowledge of the probe:
 * useSessionMonitor owns that, and this keeps the dependency pointing one way.
 */
export const registerSuspectHandler = (handler: SuspectHandler): (() => void) => {
  suspectHandlers.add(handler);

  return () => suspectHandlers.delete(handler);
};

/**
 * Tracks whether this tab still has a usable session.
 *
 * Deliberately conservative: a single failing request only ever marks the
 * session *suspect*, which schedules a probe. Nothing but a probe that comes
 * back unauthenticated (or an iframe landing on the login page, which is
 * unambiguous) moves it to `expired` and blocks the UI. That keeps per-route
 * 403s, network blips and aborted requests from throwing up a login modal.
 */
export const useSessionStore = defineStore('session', () => {
  const status = ref<SessionStatus>('active');
  const signal = ref<SessionSignal | null>(null);

  /** Who and what this tab loaded as, so we can spot a different user signing in. */
  const bootUserId = ref<number | null>(null);
  const bootUsername = ref('');

  const authProvider = ref<AuthProvider>('apx');
  const impersonating = ref(false);

  /** A channel failed to subscribe while the session was down; live updates may be stale. */
  const echoDegraded = ref(false);

  /** Set before any programmatic reload so App.vue's beforeunload handler stays quiet. */
  const suppressLeaveWarning = ref(false);

  const markSuspect = (reason: SessionSignal) => {
    // Already known to be gone — nothing to escalate.
    if (status.value === 'expired') {
      return;
    }

    status.value = 'suspect';
    signal.value = reason;

    suspectHandlers.forEach(handler => handler(reason));
  };

  const markExpired = (reason: SessionSignal) => {
    if (status.value === 'expired') {
      return;
    }

    status.value = 'expired';
    signal.value = reason;
  };

  const markUnreachable = () => {
    // Never downgrade a known expiry to a connectivity problem: if we already
    // know the session is gone, the modal should keep asking for a password.
    if (status.value === 'expired') {
      return;
    }

    status.value = 'unreachable';
  };

  const markActive = () => {
    status.value = 'active';
    signal.value = null;
  };

  return {
    authProvider,
    bootUserId,
    bootUsername,
    echoDegraded,
    impersonating,
    markActive,
    markExpired,
    markSuspect,
    markUnreachable,
    signal,
    status,
    suppressLeaveWarning,
  };
});
